import * as Sentry from "@sentry/browser";
import { Col, Descriptions, message, Row, Spin } from "antd";
import { FormInstance } from "antd/lib/form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import moment from "moment";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router";
import AttachmentsSection from "../components/AttachmentsSection";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  ASSIGN_ATTACHMENT_TO_TRANSFER_REQUEST,
  CREATE_TRANSFER_REQUEST,
} from "../gql/transfer-requests";
import {
  ValidateAccount,
  Activity,
  AssociatedEmail,
  AssociatedPhone,
  Attachment,
  BranchMap,
  Category,
  Company,
  Customer,
  Department,
  FinancialInstitution,
  FormFieldConfig,
  Id,
  PaymentAccountTypesEnum,
  Product,
  SelectOptionsInterface,
  Subscription,
  TransferRequest,
  TransferTypeEnum,
  Trustee,
  TrusteeContactPerson,
  TrusteeScheme,
  User,
  APIError,
  TransferRequestAttachment,
  CustomerCompanyAssociation,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  errorsHasSignature,
  formatSnakeCase,
  generateContactTypes,
  generateDisplayErrorMessage,
  generateTransferTypes,
  handleValidationErrors,
  injectKey,
  omitValue,
  paymentAccountTypes,
  request,
  responseHandler,
  filterActiveSubscriptions,
} from "../helpers/utilities";
import {
  CREATE_ATTACHMENT,
  GET_FINANCIAL_INSTITUTIONS,
  GET_TRUSTEES,
  GET_CATEGORIES,
  GET_ID_TYPES,
} from "../gql/general";
import { ALL_USERS_QUERY } from "../gql/users";
import { GET_DEPARTMENTS } from "../gql/departments";
import { SEARCH_COMPANY } from "../gql/companies";
import { CHECK_CUSTOMER_CONTRIBUTION, SEARCH_CUSTOMER } from "../gql/customers";
import { SEARCH_PRODUCTS } from "../gql/products";
import { VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH } from "../gql/accounts";
import { PAYOUT_VALIDATION_THRESHOLD } from "../helpers/constants";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface State {
  isLoading: boolean;
  transferRequests: TransferRequest[];
  products: Product[];
  trustees: Trustee[];
  companies: Company[];
  customers: Customer[];
  searchTerm: string;
  userSearchTerm: string;
  isSearching: boolean;
  transferTypeFieldsToHide: string[];
  requestorTypeFieldsToHide: string[];
  contactTypeFieldsToHide: string[];
  requestorType: string;
  requestorTypeInitialValue: string;
  contactType: string;
  transferType: string;
  transferTypeInitialValue: string;
  attachmentIds: string[];
  attachments: Attachment[];
  selectedCustomer?: Customer;
  selectedCompany?: Company;
  schemes: TrusteeScheme[];
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  paymentAccountType: string;
  payoutFinancialInstitutionId: string;
  idTypes: Id[];
  category: Category | undefined;
  users: User[];
  fromCompanyIdTemp: string;
  toCompanyIdTemp: string | undefined;
  transferWithinType: string;
  trusteeSchemeId: string | undefined;
  isEzhome: boolean;
  accountNumber: string
  accountValidationResponse?: ValidateAccount
  institutionOptions: FinancialInstitution[];
  payoutBranches: SelectOptionsInterface[];
  idUrl?: string
  proofOfContributionDocumentUrl?: string
  taxWaiverFieldsToHide: string[];
  taxWaiverNpraUrl?: string
  bypassValidation: boolean
}

interface RouteParams {
  id?: string;
}

export interface Props extends RouteComponentProps<RouteParams> {
  name?: string;
  user: User;
  customerActivityId?: string;
  companyInteractionId?: string;
  customer?: Customer;
  company?: Company;
  companyInteraction?: Activity;
  customerInteraction?: Activity;
  handleSuccess?: () => void;
}

const fieldsToBeValidated = [
  "requestDate",
  "transferType",
  "authorizationFormUrl",
  "identificationFormUrl",
  "companyId",
  "customerId",
  "trusteeId",
  "trusteeSchemeId",
  "contactType",
  "email",
  "phone",
  "idTypeId",
  "idNumber",
  "idUrl",
  "transferWithinType",
  "toProductId",
  "fromProductId",
  "toCompanyId",
  "toCompanyName",
  "fromCompanyId",
  "fromCompanyName",
  "productId",
  "accountType",
  "name",
  "financialInstitutionId",
  "accountNumber",
  "accountName",
  "bypassValidation",
  "verifyPayoutAccount",
  "bankBranchId",
  "firstContributionDate",
  "proofOfContributionDocumentUrl",
  "amount",
  "subCategoryId",
  "userId",
  "ticketLinks",
  "waiveOffTax",
  "taxWaiverNpraUrl",
];

const transferWithinFieldsToHide = [
  "transferWithinType",
  "toProductId",
  "fromProductId",
  "toCompanyId",
  "toCompanyName",
  "productId",
  "waiveOffTax",
  "taxWaiverNpraUrl",
];

const companyWithinFieldsToHide = [
  "transferWithinType",
  "toCompanyId",
  "fromCompanyId",
  "productId",
  "trusteeId",
  "trusteeSchemeId",
  "fromCompanyName",
  "waiveOffTax",
  "taxWaiverNpraUrl"
];

const paymentAccountFieldsToHide = [
  "accountType",
  "name",
  "financialInstitutionId",
  "accountNumber",
  "bankBranchId",
  "accountName",
  "bypassValidation",
  "verifyPayoutAccount"
];

export default class CreateTransferRequest extends React.Component<
  Props,
  State
> {
  reference: RefObject<FormInstance>;
  attachmentsReference: RefObject<FormInstance>;

  constructor(props: Props) {
    super(props);
    this.state = {
      requestorTypeInitialValue: "",
      transferTypeInitialValue: "",
      isLoading: true,
      transferRequests: [],
      products: [],
      trustees: [],
      companies: [],
      customers: [],
      searchTerm: "",
      userSearchTerm: "",
      isSearching: false,
      transferTypeFieldsToHide: transferWithinFieldsToHide,
      requestorTypeFieldsToHide: fieldsToBeValidated,
      contactTypeFieldsToHide: ["phone", "email"],
      contactType: "",
      requestorType: "",
      transferType: "",
      payoutFinancialInstitutionId: "",
      paymentAccountType: "",
      branchMap: {},
      financialInstitutions: [],
      selectedCustomer: undefined,
      attachmentIds: [],
      attachments: [],
      schemes: [],
      idTypes: [],
      category: undefined,
      users: [],
      fromCompanyIdTemp: "",
      toCompanyIdTemp: undefined,
      transferWithinType: "",
      trusteeSchemeId: undefined,
      isEzhome: false,
      accountNumber: "",
      institutionOptions: [],
      payoutBranches: [],
      taxWaiverFieldsToHide: [],
      bypassValidation: false
    };

    this.createTransferRequest = this.createTransferRequest.bind(this);
    this.reference = React.createRef<FormInstance>();
    this.attachmentsReference = React.createRef<FormInstance>();
  }

  handleContactTypeChange = (contactType: string): void => {
    let contactTypeFieldsToHide: string[] = [];

    switch (contactType) {
      case "phone":
        contactTypeFieldsToHide = ["email"];
        break;
      case "email":
        contactTypeFieldsToHide = ["phone"];
        break;
      default:
        contactTypeFieldsToHide = [];
        break;
    }

    this.setState({
      contactType,
      contactTypeFieldsToHide,
    });
  };

  handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ accountNumber: event.target.value });
  };

  handleRequestorTypeChange = (requestorType: string): void => {
    let requestorTypeFieldsToHide: string[] = [];

    switch (requestorType) {
      case "customer":
        requestorTypeFieldsToHide = ["companyId"];
        break;
      case "company":
        requestorTypeFieldsToHide = ["customerId"];
        break;
      default:
        requestorTypeFieldsToHide = ["fromCompanyName", "toCompanyName"];
        break;
    }

    this.setState({
      requestorType,
      requestorTypeFieldsToHide,
      transferTypeFieldsToHide: [
        ...transferWithinFieldsToHide,
        ...paymentAccountFieldsToHide,
      ],
    });
  };

  getFieldsToHideBasedOnTransferType = (transferType: string): string[] => {
    const { requestorType } = this.state;
    const showOnlyForTransferIn = [
      "firstContributionDate",
      "proofOfContributionDocumentUrl",
      "fromCompanyName",
    ];
    let transferTypeFieldsToHide: string[] = [];
    let inFields = omitValue(
      omitValue(transferWithinFieldsToHide, "toProductId"),
      "toCompanyId",
    );
    inFields = [...inFields, ...paymentAccountFieldsToHide];

    let withinFields = omitValue(omitValue(
      omitValue(
        transferWithinFieldsToHide,
        "transferWithinType",
      ), "waiveOffTax"
    ), "taxWaiverNpraUrl")

    withinFields = [
      ...withinFields,
      ...paymentAccountFieldsToHide,
      "trusteeId",
      "trusteeSchemeId",
    ];
    let outFields = omitValue(
      omitValue(transferWithinFieldsToHide, "fromProductId"),
      "toCompanyName",
    );

    let preservationFields = omitValue(omitValue(
      omitValue(
        transferWithinFieldsToHide,
        "fromProductId",
      ), "waiveOffTax",
    ), "taxWaiverNpraUrl")

    preservationFields = [
      ...preservationFields,
      ...paymentAccountFieldsToHide,
      "trusteeId",
      "trusteeSchemeId",
    ];

    if (requestorType === "company") {
      withinFields = companyWithinFieldsToHide;
      inFields = [...inFields, "companyId"];
      outFields = [...outFields, "companyId"];
      preservationFields = [...preservationFields, "companyId"];
    }

    const extraTransferInFieldsToHide = ["fromCompanyId", "toCompanyName"];
    const extraTransferOutFieldsToHide = ["toCompanyId", "fromCompanyName"];
    const extraTransferWithinFieldsToHide = [
      "fromCompanyName",
      "toCompanyName",
    ];
    const extraEzhomeFieldsToHide = [
      "trusteeId",
      "trusteeSchemeId",
      "transferWithinType",
      "productId",
      "waiveOffTax",
      "taxWaiverNpraUrl"
    ];

    switch (transferType) {
      case TransferTypeEnum.transferIn:
        transferTypeFieldsToHide = [
          ...inFields,
          ...extraTransferInFieldsToHide,
        ];
        this.setState({ isEzhome: false });
        break;
      case TransferTypeEnum.transferOut:
        transferTypeFieldsToHide = [
          ...outFields,
          ...showOnlyForTransferIn,
          ...extraTransferOutFieldsToHide,
        ];
        this.setState({ isEzhome: false });
        break;
      case TransferTypeEnum.transferWithin:
        transferTypeFieldsToHide = [
          ...withinFields,
          ...showOnlyForTransferIn,
          ...extraTransferWithinFieldsToHide,
        ];
        this.setState({ isEzhome: false });
        break;
      case TransferTypeEnum.transferToPreservation:
        transferTypeFieldsToHide = [
          ...preservationFields,
          ...showOnlyForTransferIn,
          ...extraTransferWithinFieldsToHide,
        ];
        this.setState({ isEzhome: false });
        break;
      case TransferTypeEnum.transferToEzhome:
        transferTypeFieldsToHide = [
          ...showOnlyForTransferIn,
          ...extraEzhomeFieldsToHide,
          ...extraTransferWithinFieldsToHide,
        ];
        this.setState({ isEzhome: true });
        break;
    }

    return transferTypeFieldsToHide;
  };

  setInitialSelectedCustomerOrCompany = (): void => {
    if (this.props.company) {
      this.setState({ selectedCompany: this.props.company });
    } else {
      this.setState({ selectedCustomer: this.props.customer });
    }
  };

  handleTransferTypeChange = (transferType: string): void => {
    const fieldsToHide: string[] =
      this.getFieldsToHideBasedOnTransferType(transferType);

    this.setState({
      transferType,
      transferTypeFieldsToHide: fieldsToHide,
    });
  };

  handleTaxWaiveChange = async (value: string) => {
    const canWaiveTax = value === "yes";

    this.setState((prevState) => ({
      taxWaiverFieldsToHide: canWaiveTax
        ? prevState.taxWaiverFieldsToHide.filter((item) => item !== "taxWaiverNpraUrl")
        : ["taxWaiverNpraUrl"],
    }));

    const { selectedCustomer } = this.state;
    interface Response {
      data: {
        checkCustomerFirstContribution: boolean;
      };
    }

    this.setState({ isSearching: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: CHECK_CUSTOMER_CONTRIBUTION,
        variables: {
          customerId: selectedCustomer?.id,
          waiveOffTax: canWaiveTax,
        },
      });

      const hasMaturedContribution =
        response.data.data.checkCustomerFirstContribution;

      if (!hasMaturedContribution) {
        this.setState({
          taxWaiverFieldsToHide: [],
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isSearching: false });
  };

  handleTransferWithinTypeChange = (transferWithinType: string): void => {
    let fieldsToHide: string[] = [];
    if (transferWithinType === "companyToCompany") {
      fieldsToHide = [
        "trusteeId",
        "trusteeSchemeId",
        "toProductId",
        "fromProductId",
        "fromCompanyName",
        "toCompanyName",
      ];
    } else {
      fieldsToHide = [
        "trusteeId",
        "trusteeSchemeId",
        "toCompanyId",
        "productId",
        "fromCompanyName",
        "toCompanyName",
      ];
      this.setState({ transferWithinType: "productToPrdouct" });
    }
    this.setState({
      transferTypeFieldsToHide: fieldsToHide,
      transferWithinType,
    });
  };

  handleInitialTransferTypeChange = (transferType: string): void => {
    const fieldsToHide: string[] =
      this.getFieldsToHideBasedOnTransferType(transferType);

    this.setState({
      transferTypeInitialValue: transferType,
      transferTypeFieldsToHide: fieldsToHide,
    });
  };

  setRef = (
    name: "reference" | "attachmentsReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  redirectToAllTransfersPage = (): void => {
    this.props.history.push("/transfer-requests");
  };

  redirectToViewTransferPage = (id: string): void => {
    this.props.history.push(`/transfer-requests/${id}`);
  };

  getTrustees(): void {
    request({
      query: GET_TRUSTEES,
    }).then(({ data: { data } }: any): void => {
      this.setState({
        trustees: data.trustees,
        isLoading: false,
      });
    });
  }

  getAllProducts(): void {
    request({
      query: SEARCH_PRODUCTS,
      variables: {
        page: 1,
        q: this.state.searchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        products: data.products.results,
        isLoading: false,
      });
    });
  }

  handleSearchInput = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, () => this.triggerDelayedSearch());
  };

  handleSearchInputProduct = async (searchTerm: string): Promise<void> => {
    this.setState({ searchTerm }, () => this.triggerDelayedSearchProduct());
  };

  handleCustomerSelect = async (id: string): Promise<void> => {
    const selectedCustomer = this.state.customers.find(
      (customer: Customer): boolean => customer.id === id,
    );

    if (this.state.requestorType === "company") {
      this.setState({ selectedCustomer });
    } else {
      this.setState({ selectedCustomer, selectedCompany: undefined });
    }
  };

  handleCompanySelect = async (id: string): Promise<void> => {
    const selectedCompany = this.state.companies.find(
      (company: Company): boolean => company.id === id,
    );
    const customerPage = window.location.pathname.includes("/customers/")

    if (this.state.requestorType === "customer" || customerPage) {
      let initialToCompanyIdTemp = undefined;
      if (this.state.transferType === "transfer_in") {
        initialToCompanyIdTemp = id
      }

      this.setState({ selectedCompany, fromCompanyIdTemp: id, toCompanyIdTemp: initialToCompanyIdTemp });
    } else {
      this.setState({
        selectedCompany,
        selectedCustomer: undefined,
        fromCompanyIdTemp: id,
        toCompanyIdTemp: undefined,
      });
    }
  };

  getPrimaryPhone = (): string | undefined => {
    let phones: AssociatedPhone[] = [];

    if (this.state.selectedCustomer) {
      phones = this.state.selectedCustomer.activeCustomerPhones ?? [];
    }

    if (this.state.selectedCompany) {
      phones = this.state.selectedCompany.companyPhones ?? [];
    }

    const phone = phones.find((p: AssociatedPhone): boolean => p.isPrimary)
      ?.phone?.number;

    return phone;
  };

  getPrimaryEmail = (): string | undefined => {
    let emails: AssociatedEmail[] = [];

    if (this.state.selectedCustomer) {
      emails = this.state.selectedCustomer.customerEmails ?? [];
    }

    if (this.state.selectedCompany) {
      emails = this.state.selectedCompany.companyEmails ?? [];
    }

    const email = emails.find((e: AssociatedEmail): boolean => e.isPrimary)
      ?.email?.address;

    this.reference.current?.setFieldsValue({ email });

    return email;
  };

  handleTransferWithinCompanySearch = debounce(
    async (searchTerm: string): Promise<void> => {
      this.setState({ searchTerm }, () => this.triggerCompanySearch());
    },
    DEBOUNCE_TIME,
  );

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    if (this.state.requestorType === "customer") {
      return await this.triggerCustomerSearch();
    } else {
      return await this.triggerCompanySearch();
    }
  }, DEBOUNCE_TIME);

  triggerDelayedSearchProduct = debounce(async (): Promise<void> => {
    return this.getAllProducts();
  }, DEBOUNCE_TIME);

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllUsers();
  }, DEBOUNCE_TIME);

  triggerCompanySearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Company[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_COMPANY,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "companySearch");

      if (err) {
        message.error(`company search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          companies: mutation.results,
        });
      }
    } catch (error) {
      message.error(`company search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearching: false });
    }

  };

  triggerCustomerSearch = async (): Promise<void> => {
    const { searchTerm: q } = this.state;
    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customers: mutation.results,
        });
      }
    } catch (error) {
      message.error(`company search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearching: false });
    }
  };

  assignAttachmentToTransferRequest = async (
    attachmentIds: string[],
    transferRequestId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToTransferRequest: {
          transferRequest: {
            transferRequestAttachments: TransferRequestAttachment[]
          };
        };
      }
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_TRANSFER_REQUEST,
      variables: {
        attachmentIds,
        transferRequestId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        isLoading: false,
        attachments:
          data.data.assignAttachmentsToTransferRequest.transferRequest.transferRequestAttachments?.map(
            (requestAttachment: TransferRequestAttachment) => requestAttachment.attachment,
          ),
      });
    });
  };


  verifyAccount = async (): Promise<void> => {
    interface Response {
      data: {
        validatePayoutAccountFuzzyMatch: ValidateAccount;
      };
    }


    const fieldsToBeValidated = [
      "financialInstitutionId",
      "accountNumber",
    ];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });


    this.setState({ isLoading: true });

    try {
      await request({
        query: VALIDATE_PAYOUT_ACCOUNT_FUZZY_MATCH,
        variables: {
          ...data,
          customerId: this.state.selectedCustomer?.id,
        },
      }).then(({ data }: AxiosResponse<Response>): void => {
        const response = data.data.validatePayoutAccountFuzzyMatch;
        const { status, score, name } = response;

        if (status === true) {
          if (score >= PAYOUT_VALIDATION_THRESHOLD || this.state.bypassValidation) {
            this.setState({ accountValidationResponse: response });
            message.success("Account verified successfully");
            message.success(`Account name: ${name} (Match Score: ${Math.round(score)}%)`);
          } else {
            this.setState({ accountValidationResponse: undefined });
            message.error(`Customer name and Bank account name match too low: ${Math.round(score)}%. Must be above ${PAYOUT_VALIDATION_THRESHOLD}.`);
            message.success(`Bank account name: ${name}`);
          }
        } else {
          this.setState({ accountValidationResponse: undefined });
          message.error(response.message || "Error verifying account number");
        }
      });
    } catch (error) {
      message.error(`Error verifying account number - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.setState({ isLoading: false });
    }
  };


  createTransferRequest = async (): Promise<void> => {
    const { accountNumber, accountValidationResponse, idUrl, proofOfContributionDocumentUrl, attachmentIds, taxWaiverNpraUrl } = this.state;

    interface Response {
      transferRequest: TransferRequest;
      errors: APIError[];
    }

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields([
        ...fieldsToBeValidated,
        "requestorType",
      ]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    if (data["ticketLinks"])
      data["ticketLinks"] = data["ticketLinks"]?.split(",");

    let variables = {
      ...data,
      contactInformation: data[this.state.contactType],
      customerInteractionId: this.props.customerActivityId,
      companyInteractionId: this.props.companyInteractionId,
      accountNumber,
      idUrl,
      taxWaiverNpraUrl,
      proofOfContributionDocumentUrl,
      name: accountValidationResponse?.name,
      waiveOffTax: data.waiveOffTax === "yes",
    };

    if (data.transferWithinType === "companyToCompany") {
      variables = {
        ...variables,
        fromProductId: data.productId,
        toProductId: data.productId,
      };
    }

    try {
      const response: AxiosResponse = await request({
        query: CREATE_TRANSFER_REQUEST,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createTransferRequest",
      );

      const error = err || mutation.errors;

      if (mutation.transferRequest) {
        message.success('Transfer request created successfully');
        if (attachmentIds.length > 0) {
          this.assignAttachmentToTransferRequest(
            attachmentIds,
            mutation.transferRequest.id,
          );
        }
        this.reference.current.resetFields();
        this.props.handleSuccess?.()
      } else {
        message.error('Failed to create transfer request');
        message.error(generateDisplayErrorMessage(error));
        if (errorsHasSignature(error, "contactInformation")) {
          this.reference.current.setFields([
            {
              name: "email",
              errors: ["email is invalid or unreachable"],
            },
            {
              name: "phone",
              errors: ["phone is invalid or unreachable"],
            },
          ]);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  componentDidMount(): void {
    this.getAllProducts();
    this.getTrustees();
    this.getFinancialInstitutionals();
    this.getIdTypes();
    this.getCategories();
    this.getAllUsers();
    if (this.props.customer || this.props.company) {
      this.getDefaultRequestorType();
      this.getDefaultTransferType();
      this.setInitialSelectedCustomerOrCompany();
    }
  }

  createAttachment = async (combinedInfo: string): Promise<void> => {
    interface Response {
      attachment: Attachment;
    }

    const fieldsToBeValidated: string[] = ["url"];

    this.setState({ isLoading: true });

    try {
      const [url, name] = combinedInfo.split("@@@");

      const response: AxiosResponse = await request({
        query: CREATE_ATTACHMENT,
        variables: {
          url,
          name,
        },
      });
      const { mutation } = responseHandler<Response>(
        response,
        "createAttachment",
      );
      this.setState((state) => ({
        attachmentIds: [...state.attachmentIds, mutation.attachment.id],
        attachments: [...state.attachments, mutation.attachment],
      }));
      this.reference.current &&
        this.reference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  removeAttachment = (attachmentId: string): void => {
    this.setState((state) => ({
      attachments: state.attachments.filter(
        (attachment) => attachment.id !== attachmentId,
      ),
      attachmentIds: state.attachmentIds.filter((id) => id !== attachmentId),
    }));
  };

  getDefaultRequestorType = (): void => {
    let entity = "";
    let requestorTypeFields: string[] = [];
    if (this.props.customer) {
      requestorTypeFields = ["companyId"];
      entity = "customer";
    } else if (this.props.company) {
      requestorTypeFields = ["customerId"];
      entity = "company";
    } else {
      requestorTypeFields = [];
      entity = "";
    }

    this.setState({
      requestorTypeFieldsToHide: requestorTypeFields,
      requestorTypeInitialValue: entity,
    });
    this.reference.current?.setFieldsValue({ requestorType: entity });
  };

  getRequestDate = (): moment.Moment | undefined => {
    let requestDate;
    if (this.props.customerInteraction) {
      requestDate = moment(this.props.customerInteraction.activityDate);
    } else if (this.props.companyInteraction) {
      requestDate = moment(this.props.companyInteraction.activityDate);
    }

    return requestDate;
  };

  getDefaultTransferType = (): void => {
    const { customerInteraction, companyInteraction } = this.props;
    const m: { [x: string]: string } = {
      "request to transfer in": TransferTypeEnum.transferIn,
      "request to transfer out": TransferTypeEnum.transferOut,
      "request to merge accounts": TransferTypeEnum.transferWithin,
      "request for company transfer out": TransferTypeEnum.transferOut,
    };

    let name = "";
    if (customerInteraction) {
      name = customerInteraction.subCategory.name.toLowerCase();
    } else if (companyInteraction) {
      name = companyInteraction.subCategory.name.toLowerCase();
    }

    this.handleInitialTransferTypeChange(m[name]);
  };

  getTrusteeSchemes = (id: string): void => {
    const { trustees } = this.state;
    const trustee = trustees.find((t: Trustee): boolean => t.id === id);
    trustee && this.setState({ schemes: trustee?.trusteeSchemes ?? [] });
  };

  getTrusteeContactInformation = (id: string | undefined) => {
    const { schemes } = this.state;
    return schemes.find((t) => t?.id === id)
      ?.trusteeContactPerson as TrusteeContactPerson;
  };

  handleTrusteeScheme = (id: string): void => {
    this.setState({ trusteeSchemeId: id });
  };

  handlePayoutFinancialInstitutionChange = (
    payoutFinancialInstitutionId: string,
  ): void => {
    this.setState({ payoutBranches: this.state.branchMap[payoutFinancialInstitutionId], payoutFinancialInstitutionId });
  };

  handleBypassValidationChange = async (checkedValue: boolean) => {
    this.setState({bypassValidation: checkedValue});
  };

  generateBranchMap = async (): Promise<void> => {
    const branchMap: BranchMap = {};

    this.state.financialInstitutions.forEach(
      (institution: FinancialInstitution): void => {
        branchMap[institution.id] = institution.bankBranches;
      },
    );

    this.setState({ branchMap });
  };

  getFinancialInstitutionals = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }
    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      this.setState({
        financialInstitutions: data.financialInstitutions,
        isLoading: false,
      });
      this.generateBranchMap();
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getIdTypes = async (): Promise<void> => {
    interface Response {
      data: {
        idTypes: Id[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_ID_TYPES,
      });

      this.setState({
        idTypes: data.idTypes,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getDepartments = async (): Promise<Department[]> => {
    interface Response {
      data: {
        departments: Department[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_DEPARTMENTS,
      });

      return data.departments;
    } catch (error) {
      Sentry.captureException(error);
    }
    return [];
  };

  getCategories = async (): Promise<void> => {
    interface Response {
      data: {
        categories: Category[];
      };
    }

    try {
      this.setState({ isLoading: true });

      const departments = await this.getDepartments();

      if (departments.length > 0) {
        const clDepartmet = departments.find(
          (department: Department): boolean => {
            return (
              department.name.toLowerCase() === "client service" ||
              department.name.toLowerCase() === "client services"
            );
          },
        );

        const {
          data: { data },
        }: AxiosResponse<Response> = await request({
          query: GET_CATEGORIES,
          variables: {
            departmentId: clDepartmet?.id,
          },
        });
        const category = data.categories.find((category: Category): boolean => {
          return category.name.toLowerCase() === "redemptions and transfers";
        });
        this.setState({
          category: category,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  handlePaymentTypeSelect = (paymentAccountType: string): void => {
    let financialInstitutions: FinancialInstitution[] = [];
    if (paymentAccountType === "mm_account") {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "mobile_money";
        },
      );
    } else {
      financialInstitutions = this.state.financialInstitutions.filter(
        (institution: FinancialInstitution): boolean => {
          return institution.institutionType === "bank";
        },
      );
    }
    this.setState({ institutionOptions: financialInstitutions });
  };

  handlePaymentTypeChange = (): void => {
    this.setState({ accountNumber: "", accountValidationResponse: undefined, payoutFinancialInstitutionId: "" });
  };

  getAllUsers = (): void => {
    request({
      query: ALL_USERS_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        users: data.users.results,
        isLoading: false,
      });
    });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => this.triggerUserDelayedSearch())
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url
    }));
  }

  filterSubscriptions = (subscriptions: Subscription[]) => {
    const active = filterActiveSubscriptions(subscriptions);

    return this.state.transferWithinType === "productToProduct"
      ? active
        ?.filter(
          (filterItem) =>
            filterItem.company?.id === this.state.fromCompanyIdTemp,
        )
        ?.map((item) => item.product) ?? []
      : active?.map((item) => item.product) ?? [];
  };

  render(): JSX.Element {
    const {
      isLoading,
      products,
      trustees,
      companies,
      isSearching,
      searchTerm,
      transferTypeFieldsToHide,
      requestorTypeFieldsToHide,
      contactTypeFieldsToHide,
      customers,
      schemes,
      requestorTypeInitialValue,
      transferTypeInitialValue,
      category,
      users,
      trusteeSchemeId,
      isEzhome,
      paymentAccountType,
      accountValidationResponse,
      payoutBranches,
      financialInstitutions,
      transferType,
      taxWaiverFieldsToHide,
      bypassValidation
    } = this.state;

    const fileList =
      this.props.customer?.photoIdUrl && this.props.customer?.idType
        ? [
          {
            size: 1000,
            type: this.props.customer?.idType.name,
            uid: "1",
            url: this.props.customer?.photoIdUrl,
            name: this.props.customer?.idType.name,
            thumbUrl: this.props.customer?.photoIdUrl,
          },
        ]
        : [];

    let modifiedProducts: Product[] = injectKey<Product>(products, "name");
    let modifiedCompanies: Company[] = injectKey<Company>(companies, "name");
    const customer = this.props.customer || this.state.selectedCustomer;
    const company = this.props.company || this.state.selectedCompany;

    if (customer) {
      const customerSubscriptions = customer.customerSubscriptions;
      const filteredCustomerSubscriptions = this.filterSubscriptions(
        customerSubscriptions ?? [],
      );
      modifiedProducts = injectKey<Product>(
        filteredCustomerSubscriptions,
        "name"
      );

      if (this.state.transferType === "transfer_in") {
        const myCompany = customer?.customerCompanyAssociations.find(
          (cc: CustomerCompanyAssociation): boolean => cc.company.id === this.state.toCompanyIdTemp
        );

        const filteredCompanySubscriptions = this.filterSubscriptions(
          myCompany?.company.companySubscriptions ?? [],
        );

        modifiedProducts = injectKey<Product>(
          filteredCompanySubscriptions,
          "name",
        );

      }


      modifiedCompanies = injectKey<Company>(
        customer.customerCompanyAssociations?.map((item) => item.company) ?? [],
        "name",
      );
    }

    if (company) {
      const companySubscriptions = company.companySubscriptions;
      const filteredCompanySubscriptions = this.filterSubscriptions(
        companySubscriptions ?? [],
      );
      modifiedProducts = injectKey<Product>(
        filteredCompanySubscriptions,
        "name",
      );

      modifiedCompanies = injectKey<Company>(companies, "name");
    }

    const transferWithinTypes: SelectOptionsInterface[] = [
      {
        id: "productToPrdouct",
        name: "Product To Product",
      },
      {
        id: "companyToCompany",
        name: "Company To Company",
      },
    ];

    const trusteeInfo = this.getTrusteeContactInformation(trusteeSchemeId);

    const config: FormFieldConfig[] = [
      {
        id: "requestorType",
        name: "requestorType",
        inputType: "select",
        label: "Requestor Type",
        rule: "string",
        placeholder: "Requestor Type",
        changeHandler: this.handleRequestorTypeChange,
        required: true,
        disabled: !!(this.props.customer || this.props.company),
        value: this.state.requestorType,
        initialValue: requestorTypeInitialValue,
        colConfig: {
          span: 24,
        },
        selectOptions: [
          { id: "customer", name: "Customer" },
          { id: "company", name: "Company" },
        ],
      },
      {
        name: "transferType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Transfer Type",
        placeholder: "Transfer Type",
        value: this.state.transferType,
        initialValue: transferTypeInitialValue,
        changeHandler: this.handleTransferTypeChange,
        selectOptions: generateTransferTypes(),
        colConfig: {
          span: 24,
        },
      },

      {
        id: "transferWithinType",
        name: "transferWithinType",
        inputType: "select",
        rule: "string",
        label: "Select Transfer Within Type",
        placeholder: "Select transfer within type",
        required: true,
        changeHandler: this.handleTransferWithinTypeChange,
        selectOptions: transferWithinTypes,
        value: this.state.transferWithinType,
        colConfig: {
          span: 24,
        },
      },

      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Customer",
        disabled: !!this.props.customer,
        selectOptions: this.props.customer
          ? convertCustomersToSelectOptions([this.props.customer])
          : convertCustomersToSelectOptions(customers),
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCustomerSelect,
        initialValue: this.props.customer?.id,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "fromCompanyId",
        name: "fromCompanyId",
        inputType: "search",
        rule: "string",
        label: "From Company",
        placeholder: "Search company by name",
        required: true,
        disabled: !!this.props.company,
        selectOptions: modifiedCompanies,
        changeHandler: this.handleTransferWithinCompanySearch,
        selectHandler: this.handleCompanySelect,
        initialValue: this.props.company?.id,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "fromCompanyName",
        name: "fromCompanyName",
        inputType: "string",
        rule: "string",
        label: "From Company",
        placeholder: "Enter company name",
        required: true,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "toCompanyId",
        name: "toCompanyId",
        inputType: "search",
        rule: "string",
        label: "To Company",
        placeholder: "Search company by name",
        required: true,
        disabled: !!this.props.company,
        selectOptions: modifiedCompanies,
        changeHandler: this.handleTransferWithinCompanySearch,
        selectHandler: this.handleCompanySelect,
        initialValue: this.props.company?.id,
        value: searchTerm,
        isSearching,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "toCompanyName",
        name: "toCompanyName",
        inputType: "string",
        rule: "string",
        label: "To Company",
        placeholder: "Enter company name",
        required: true,
        isSearching,
        colConfig: {
          span: 24,
        },
      },

      {
        id: "companyId",
        name: "companyId",
        inputType: "search",
        required: true,
        placeholder: "Seach by name or petraId",
        rule: "string",
        label: "Company",
        disabled: !!this.props.company,
        selectOptions: this.props.company ? [this.props.company] : companies,
        changeHandler: this.handleSearchInput,
        selectHandler: this.handleCompanySelect,
        initialValue: this.props.company?.id,
        value: searchTerm,
        isSearching,
        colConfig: {
          span: 24,
        },
      },

      {
        id: "fromProductId",
        name: "fromProductId",
        inputType: "search",
        rule: "string",
        label: "From Product",
        placeholder: "Select Product",
        required: true,
        selectOptions: modifiedProducts,
        changeHandler: this.handleSearchInputProduct,
        colConfig: {
          span: 24,
        },
      },

      {
        id: "toProductId",
        name: "toProductId",
        inputType: "search",
        rule: "string",
        label: "To Product",
        placeholder: "Select Product",
        required: true,
        selectOptions: modifiedProducts,
        changeHandler: this.handleSearchInputProduct,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "productId",
        name: "productId",
        inputType: "select",
        rule: "string",
        label: "Select Scheme",
        placeholder: "Select Product",
        required: true,
        selectOptions: modifiedProducts,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "trusteeId",
        name: "trusteeId",
        inputType: "select",
        required: true,
        placeholder: "Trustee",
        rule: "string",
        label: "Trustee",
        selectOptions: trustees,
        changeHandler: this.getTrusteeSchemes,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "trusteeSchemeId",
        name: "trusteeSchemeId",
        inputType: "select",
        required: true,
        placeholder: "Trustee Scheme",
        rule: "string",
        label: "Trustee Scheme",
        selectOptions: schemes,
        changeHandler: this.handleTrusteeScheme,
        selectHandler: this.handleTrusteeScheme,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "userId",
        name: "userId",
        rule: "string",
        inputType: "search",
        required: true,
        label: "Assigned To",
        placeholder: "Assigned To",
        selectOptions: users,
        changeHandler: this.handleUserSearchInput,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "subCategoryId",
        name: "subCategoryId",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Request Type",
        placeholder: "Request Type",
        selectOptions: category?.subCategories,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountType",
        name: "accountType",
        placeholder: "Type",
        inputType: "select",
        rule: "string",
        selectHandler: this.handlePaymentTypeSelect,
        changeHandler: this.handlePaymentTypeChange,
        label: "Payment Account Type",
        initialValue: paymentAccountType,
        selectOptions: [
          {
            id: PaymentAccountTypesEnum.bankAccount,
            name: paymentAccountTypes.bank_account,
            value: PaymentAccountTypesEnum.bankAccount,
          },
        ],
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "financialInstitutionId",
        name: "financialInstitutionId",
        placeholder: "Institution Name",
        inputType: "select",
        selectHandler: this.handlePayoutFinancialInstitutionChange,
        selectOptions: financialInstitutions,
        rule: "string",
        label: "Institution Name",
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "bankBranchId",
        name: "bankBranchId",
        placeholder: "Branch",
        inputType: "select",
        rule: "string",
        label: "Branch",
        selectOptions: payoutBranches,
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountNumber",
        name: "accountNumber",
        placeholder: "Account Number",
        inputType: "string",
        rule: "string",
        label: "Account Number",
        onChange: this.handleAccountNumberChange,
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "bypassValidation",
        name: "bypassValidation",
        inputType: "checkbox",
        required: false,
        rule: "boolean",
        label: "Third Party?",
        checkboxCheckedValue: bypassValidation,
        checkboxChangeHandler: (e) => this.handleBypassValidationChange(e.target.checked),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "accountName",
        name: "accountName",
        inputType: "description",
        required: false,
        rule: "string",
        label: "Account Name",
        value: accountValidationResponse?.name,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "verifyPayoutAccount",
        name: "verifyPayoutAccount",
        inputType: "button",
        className: "base-button",
        required: false,
        handleClick: this.verifyAccount,
        value: "Verify",
        elementStyle: {
          position: "relative",
          textAlign: "left",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        name: "waiveOffTax",
        rule: "string",
        inputType: "select",
        required: false,
        label: "Should Tax Be Waived Off?",
        placeholder: "Should Tax Be Waived Off?",
        initialValue: "yes",
        changeHandler: this.handleTaxWaiveChange,
        selectOptions: [
          { id: "yes", name: "Yes" },
          { id: "no", name: "No" },
        ],
        colConfig: {
          span: 24,
        },
      },
      {
        name: "taxWaiverNpraUrl",
        label: "Upload Contribution Verification Document",
        inputType: "fileUpload",
        defaultFileList: [],
        placeholder: "Upload Contribution Verification Document",
        rule: "string",
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("taxWaiverNpraUrl", combinedInfo),
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        name: "contactType",
        rule: "string",
        inputType: "select",
        required: true,
        label: "Contact Type",
        placeholder: "Contact Type",
        changeHandler: this.handleContactTypeChange,
        selectOptions: generateContactTypes(),
        colConfig: {
          span: 24,
        },
      },
      {
        id: "email",
        name: "email",
        inputType: "string",
        rule: "email",
        label: "Transfer notification sent to",
        placeholder: "Enter email address ...",
        required: true,
        value: this.getPrimaryEmail(),
        colConfig: {
          span: 24,
        },
      },
      {
        id: "phone",
        name: "phone",
        inputType: "phone",
        rule: "string",
        label: "Transfer notification sent to",
        placeholder: "Enter phone number",
        value: this.getPrimaryPhone(),
        required: false,
        colConfig: {
          span: 24,
        },
      },
      {
        id: "firstContributionDate",
        name: "firstContributionDate",
        inputType: "defaultDate",
        required: false,
        rule: "object",
        label: "Select First Contribution Date",
        initialValue: this.getRequestDate(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        name: "proofOfContributionDocumentUrl",
        label: "Upload Proof Of Contribution Document",
        inputType: "fileUpload",
        defaultFileList: fileList,
        placeholder: "Upload Proof Of Contribution Document",
        rule: "string",
        required: false,
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("proofOfContributionDocumentUrl", combinedInfo),
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idTypeId",
        name: "idTypeId",
        inputType: "select",
        rule: "string",
        required: false,
        disabled: false,
        placeholder: "ID Type",
        selectOptions: this.state.idTypes,
        initialValue: this.props.customer?.idType?.name,
        label: "ID Type",
        colConfig: {
          span: 24,
        },
      },
      {
        id: "idNumber",
        name: "idNumber",
        inputType: "string",
        rule: "string",
        required: false,
        disabled: false,
        placeholder: "ID Number",
        initialValue: this.props.customer?.photoIdNumber,
        label: "ID Number",
        colConfig: {
          span: 24,
        },
      },
      {
        name: "idUrl",
        label: "Upload ID",
        inputType: "fileUpload",
        defaultFileList: fileList,
        placeholder: "Upload ID",
        rule: "string",
        required: transferType !== "transfer_to_preservation",
        changeHandler: (combinedInfo: string) => this.handleFileUploadChange("idUrl", combinedInfo),
        colConfig: {
          span: 24,
        },
      },
      {
        name: "amount",
        rule: "number",
        inputType: "number",
        required: isEzhome,
        label: "Amount",
        placeholder: "Amount",
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "requestDate",
        name: "requestDate",
        inputType: "defaultDate",
        required: true,
        rule: "object",
        label: "Request Date",
        initialValue: this.getRequestDate(),
        elementStyle: { width: "100%" },
        colConfig: {
          span: 24,
        },
      },
      {
        id: "ticketLinks",
        name: "ticketLinks",
        required: false,
        inputType: "textArea",
        rule: "string",
        label: "Ticket Links(Separated by comma)",
        placeholder: "Enter Ticket Links(Separated by comma)",
        elementStyle: {
          width: "100%",
        },
        colConfig: {
          span: 24,
        },
      },
      {
        name: "createTransferRequest",
        value: "Create Transfer Request",
        inputType: "button",
        required: false,
        elementStyle: {
          position: "relative",
          top: "10px",
          height: "33px",
        },
        className: "base-button",
        handleClick: this.createTransferRequest,
        colConfig: {
          span: 24,
        },
      },
    ];

    return (
      <Spin spinning={isLoading}>
        <Card header="New Transfer Request">
          <Row>
            <Col span={8}>
              <BaseForm
                reference={this.reference}
                fieldsToHide={[
                  ...transferTypeFieldsToHide,
                  ...requestorTypeFieldsToHide,
                  ...contactTypeFieldsToHide,
                  ...taxWaiverFieldsToHide
                ]}
                layout="vertical"
                fields={config}
              />
            </Col>
            <Col offset={1} span={15}>
              <AttachmentsSection
                reference={this.attachmentsReference}
                createAttachment={this.createAttachment}
                attachments={this.state.attachments}
                showForm={true}
                handleRemove={this.removeAttachment}
              />
              {trusteeInfo && (
                <>
                  <h2>Trustee Information</h2>
                  <Descriptions layout={"horizontal"}>
                    <Descriptions.Item label={<b>Full Name</b>}>
                      {formatSnakeCase(trusteeInfo?.firstName ?? "")}{" "}
                      {formatSnakeCase(trusteeInfo?.middleName ?? "")}{" "}
                      {formatSnakeCase(trusteeInfo?.lastName ?? "")}
                    </Descriptions.Item>
                  </Descriptions>
                  <Descriptions layout={"horizontal"}>
                    <Descriptions.Item label={<b>Email</b>}>
                      {formatSnakeCase(trusteeInfo?.email ?? "")}
                    </Descriptions.Item>
                  </Descriptions>
                  <Descriptions layout={"horizontal"}>
                    <Descriptions.Item label={<b>Phone Number</b>}>
                      {formatSnakeCase(trusteeInfo?.phoneNumber ?? "")}
                    </Descriptions.Item>
                  </Descriptions>
                </>
              )}
            </Col>
          </Row>
        </Card>
      </Spin>
    );
  }
}
